import React, { useEffect, useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, Tooltip, Avatar, Pagination
} from '@mui/material';
import { FileText, Download } from 'lucide-react';
import * as actions from './actions';
import { toastError, toastSuccess } from '../../utils';
import { API_URL } from '../../constants';
import StatusBadge from '../../components/StatusBadge';
import EmptyState from '../../components/EmptyState';
import ActionButton from '../../components/ActionButton';
import moment from 'moment';

const durationMap = { short_1hr: 60, short_1_5hr: 90, short_2hr: 120, short_2_5hr: 150, short_3hr: 180 };

const formatTimeframe = (leaveType, leaveTime) => {
  if (!leaveTime || !durationMap[leaveType]) return '';
  const [h, m] = leaveTime.split(':').map(Number);
  const startMin = h * 60 + m;
  const endMin = startMin + durationMap[leaveType];
  const fmt = (mins) => {
    let hr = Math.floor(mins / 60) % 24;
    const mn = mins % 60;
    const ampm = hr >= 12 ? 'PM' : 'AM';
    hr = hr % 12 || 12;
    return `${hr}:${String(mn).padStart(2, '0')} ${ampm}`;
  };
  return `${fmt(startMin)} - ${fmt(endMin)}`;
};

const leaveTypeLabels = {
  full_day: 'Full Day',
  half_day_morning: 'Morning',
  half_day_afternoon: 'Afternoon',
  short_1hr: '1 Hour',
  short_1_5hr: '1.5 Hours',
  short_2hr: '2 Hours',
  short_2_5hr: '2.5 Hours',
  short_3hr: '3 Hours',
};

const cellSx = {
  color: '#CBD5E1',
  fontSize: '0.82rem',
  borderBottom: '1px solid #2E3247',
  py: 1.5,
};

const headCellSx = {
  color: '#94A3B8',
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '1px solid #2E3247',
  py: 1.2,
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function getExportOptions() {
  const now = new Date();
  const options = [];

  for (let i = 0; i < 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    options.push({ label: MONTH_NAMES[d.getMonth()], value: monthStr });
  }

  options.push({ label: 'All', value: 'all' });
  return options;
}

export default function LeaveHistory({ setLoader }) {
  const [leaves, setLeaves] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);
  const limit = 50;

  const exportOptions = getExportOptions();

  useEffect(() => {
    loadHistory(page);
  }, [page]);

  const loadHistory = (p) => {
    setLoader(true);
    actions.getLeaveHistory({ page: p })
      .then((res) => {
        setLoader(false);
        setLeaves(res.body?.leaves || []);
        setTotal(res.body?.total || 0);
      })
      .catch((err) => {
        setLoader(false);
        toastError(err?.message || 'Failed to load leave history');
      });
  };

  const handleExport = (month) => {
    setExporting(true);
    actions.exportLeaveHistory(month)
      .then(() => {
        setExporting(false);
        toastSuccess('Leave history exported successfully');
      })
      .catch((err) => {
        setExporting(false);
        toastError(err?.message || 'Failed to export leave history');
      });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontSize: '0.85rem', color: '#94A3B8' }}>
          Total: <span style={{ color: '#6C63FF', fontWeight: 600 }}>{total}</span> leaves
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Typography sx={{ fontSize: '0.78rem', color: '#64748B', mr: 0.5 }}>Export:</Typography>
          {exportOptions.map((opt) => (
            <ActionButton
              key={opt.value}
              variant="info"
              disabled={exporting}
              onClick={() => handleExport(opt.value)}
              sx={{ fontSize: '0.73rem', py: 0.3, px: 1.2, gap: 0.3 }}
            >
              <Download size={12} />
              {opt.label}
            </ActionButton>
          ))}
        </Box>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Admin</TableCell>
              <TableCell sx={headCellSx}>Date</TableCell>
              <TableCell sx={headCellSx}>Type</TableCell>
              <TableCell sx={headCellSx}>Reason</TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
              <TableCell sx={headCellSx}>Review Note</TableCell>
              <TableCell sx={headCellSx}>Applied On</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaves.length === 0 ? (
              <EmptyState message="No leave history found" icon={FileText} colSpan={7} />
            ) : (
              leaves.map((leave) => (
                <TableRow key={leave.leaveId} sx={{ '&:hover': { backgroundColor: 'rgba(108, 99, 255, 0.04)' } }}>
                  <TableCell sx={cellSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={leave.photograph ? `${API_URL}/${leave.photograph}` : undefined}
                        sx={{ width: 28, height: 28, backgroundColor: '#6C63FF', fontSize: '0.7rem', fontWeight: 600 }}
                      >
                        {leave.name ? leave.name.charAt(0).toUpperCase() : '?'}
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontSize: '0.82rem', color: '#E2E8F0', fontWeight: 500, lineHeight: 1.3 }}>
                          {leave.name}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {moment(leave.leaveDate).format('DD MMM YYYY')}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Chip
                      label={leave.leaveTime ? formatTimeframe(leave.leaveType, leave.leaveTime) : (leaveTypeLabels[leave.leaveType] || 'Full Day')}
                      size="small"
                      sx={{
                        backgroundColor: '#232636',
                        color: '#94A3B8',
                        fontSize: '0.73rem',
                        border: '1px solid #2E3247',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...cellSx, maxWidth: 250 }}>
                    <Typography noWrap sx={{ fontSize: '0.82rem', color: '#CBD5E1' }}>
                      {leave.reason}
                    </Typography>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <StatusBadge status={leave.status} />
                  </TableCell>
                  <TableCell sx={{ ...cellSx, maxWidth: 200 }}>
                    {leave.reviewNote ? (
                      <Tooltip
                        title={
                          <Box>
                            <Typography sx={{ fontSize: '0.78rem', color: '#F1F5F9', whiteSpace: 'pre-wrap' }}>
                              {leave.reviewNote}
                            </Typography>
                            {leave.reviewedBy && (
                              <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', mt: 0.5 }}>
                                — {leave.reviewedBy}
                              </Typography>
                            )}
                          </Box>
                        }
                        arrow
                        placement="top"
                        slotProps={{
                          tooltip: { sx: { backgroundColor: '#1A1D27', border: '1px solid #2E3247', borderRadius: '8px', px: 1.5, py: 1, maxWidth: 300 } },
                          arrow: { sx: { color: '#1A1D27', '&::before': { border: '1px solid #2E3247' } } },
                        }}
                      >
                        <Box>
                          <Typography noWrap sx={{ fontSize: '0.78rem', color: '#CBD5E1' }}>
                            {leave.reviewNote}
                          </Typography>
                          {leave.reviewedBy && (
                            <Typography noWrap sx={{ fontSize: '0.68rem', color: '#64748B', mt: 0.3 }}>
                              by {leave.reviewedBy}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography sx={{ fontSize: '0.78rem', color: '#4A5068' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {moment(leave.createdAt).format('DD MMM YYYY')}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, val) => setPage(val)}
            sx={{
              '& .MuiPaginationItem-root': {
                color: '#94A3B8',
                borderColor: '#2E3247',
                '&.Mui-selected': {
                  backgroundColor: '#6C63FF',
                  color: '#F1F5F9',
                  '&:hover': { backgroundColor: '#5B54E0' },
                },
                '&:hover': { backgroundColor: 'rgba(108, 99, 255, 0.1)' },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
